import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useState } from 'react';
import styled from 'styled-components';

import { Blueprint, BlueprintBlockType } from 'dashboard/models/blueprint';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import {
  DashboardPageLink,
  GuideStepTipBanner,
  UnorderedList,
} from 'dashboard/views/DashboardSandboxHomeView/constants';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';

interface ReviewWorkflowRunStepProps {
  workflowRunsForSelectedBlueprint: WorkflowRun[];
  isLoadingWorkflowRunsForSelectedBlueprint: boolean;
  selectedBlueprint: Blueprint;
  isStepCompleted: boolean;
  isSavingCompletedStep: boolean;
  handleLoadWorkflowRunsForSelectedBlueprint: (workflowIds: string[]) => Promise<void>;
  saveCompletedStep: () => void;
}

const ReviewWorkflowRunStep = ({
  workflowRunsForSelectedBlueprint,
  isLoadingWorkflowRunsForSelectedBlueprint,
  selectedBlueprint,
  isStepCompleted,
  isSavingCompletedStep,
  handleLoadWorkflowRunsForSelectedBlueprint,
  saveCompletedStep,
}: ReviewWorkflowRunStepProps) => {
  const [selectedWorkflowRun, setSelectedWorkflowRun] = useState(null);

  const handleWorkflowRunChange = (value: string) => {
    const workflowRun = workflowRunsForSelectedBlueprint.filter((wfr) => wfr.id === value)[0];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setSelectedWorkflowRun(workflowRun);
  };

  return (
    <>
      <Paragraph color='primary'>
        Your solution includes a Workflow that runs every time an Inquiry completes.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        A Workflow lets you automate logic and actions. You can set up a Workflow to be triggered
        by, and act on almost any other part of Persona.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>Select a Workflow Run to see what this Workflow did:</Paragraph>
      <Spacer height={1} />
      <Select
        aria-label='Select a Workflow Run'
        disabled={
          isLoadingWorkflowRunsForSelectedBlueprint || workflowRunsForSelectedBlueprint.length === 0
        }
        placeholder={
          workflowRunsForSelectedBlueprint.length === 0
            ? 'No Workflow Runs for this solution yet!'
            : 'Choose a Workflow Run...'
        }
        onValueChange={(value) => handleWorkflowRunChange(value)}
      >
        {workflowRunsForSelectedBlueprint.map((wfr) => (
          <Option key={`option-${wfr.id}`} value={wfr.id}>
            Workflow Run created on{' '}
            {DateTime.fromISO(wfr.attributes['created-at']).toFormat(DATE_AND_TIME_TZ_FORMAT)} (
            {wfr.id})
          </Option>
        ))}
      </Select>
      <Spacer height={1} />
      <ButtonRow>
        {isSavingCompletedStep ? (
          <Button disabled width='button'>
            Saving...
          </Button>
        ) : (
          <Button
            id='home-sandbox-onboarding-checklist__view-workflow-run'
            disabled={!selectedWorkflowRun || isLoadingWorkflowRunsForSelectedBlueprint}
            width='button'
            onClick={() => {
              if (!isStepCompleted) {
                saveCompletedStep();
              }
              let url = DashboardPageLink.WorkflowRun as string;
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              url = url.replace('[workflow-run-token]', selectedWorkflowRun.id);
              url = url.replace(
                '[workflow-token]',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                selectedWorkflowRun.relationships.workflow.data.id,
              );
              window.open(url);
            }}
          >
            View the Workflow Run
          </Button>
        )}
        <Button
          id='home-sandbox-onboarding-checklist__refresh-workflow-runs-list'
          disabled={isLoadingWorkflowRunsForSelectedBlueprint}
          variant='basic'
          width='button'
          onClick={() => {
            const workflowBlocks = selectedBlueprint.attributes.blocks.filter(
              (block) => block.type === BlueprintBlockType.Workflow,
            );
            if (workflowBlocks.length > 0) {
              handleLoadWorkflowRunsForSelectedBlueprint(workflowBlocks.map((wf) => wf.token));
            }
          }}
        >
          Reload Workflow Runs
        </Button>
      </ButtonRow>
      <Spacer height={3} />
      <GuideStepTipBanner>
        <Paragraph color='primary'>
          Tip: See our Help Center to learn more about Workflows:
        </Paragraph>
        <UnorderedList>
          <li>
            <a
              href='https://support.withpersona.com/hc/en-us/articles/360041047533-Workflows-overview'
              target='_blank'
              rel='noreferrer'
            >
              Workflows overview
            </a>
          </li>
        </UnorderedList>
      </GuideStepTipBanner>
    </>
  );
};

const ButtonRow = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
`;

export default ReviewWorkflowRunStep;
